import React, { useEffect, useRef } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Row, Col } from "stories/layout";
import { Input } from "stories/forms";
import classnames from "classnames";
import "./derivativeAlignedRow.css";
import { thousandFormat, DERIVATIVE_PRICE_DECIMALS } from "services/helpers";
import { computeTerminationCashTotal } from "views/app/OptionEdit/derivativeTerminationUtils";

const TOTAL_DECIMAL_PLACES = DERIVATIVE_PRICE_DECIMALS;

export const TerminationSettlementFields = ({
  quantity,
  quantityReadOnly = true,
  onQuantityChange,
  unitPrice,
  onUnitPriceChange,
  total,
  onTotalChange,
  strikePrice,
  role,
  optionRight,
  derivativeType,
  unitClassName,
  className,
  stateDate,
}) => {
  const { t } = useTranslation();
  const quantityEditable = Boolean(onQuantityChange) && !quantityReadOnly;
  const unitEditable = Boolean(onUnitPriceChange);
  const totalEditable = Boolean(onTotalChange);
  const onTotalChangeRef = useRef(onTotalChange);
  const previousInputs = useRef({ quantity, unitPrice });
  onTotalChangeRef.current = onTotalChange;

  useEffect(() => {
    const updateTotal = onTotalChangeRef.current;
    if (!updateTotal) return;
    const quantityChanged = String(previousInputs.current.quantity ?? "") !== String(quantity ?? "");
    const unitChanged = String(previousInputs.current.unitPrice ?? "") !== String(unitPrice ?? "");
    previousInputs.current = { quantity, unitPrice };
    if (!quantityChanged && !unitChanged) return;
    updateTotal(computeTerminationCashTotal({
      quantity,
      unitPrice,
      strikePrice,
      role,
      optionRight,
      derivativeType,
    }));
  }, [quantity, unitPrice, strikePrice, role, optionRight, derivativeType]);

  return (
    <Row className={classnames("derivative-aligned-row", className)}>
      <Col xs="12" md="4" className="mb-3">
        {quantityEditable ? (
          <Input
            name="termination_settlement_quantity"
            type="number"
            min={0}
            step={1}
            label={t("termination_settlement_quantity", "Terminoitava määrä")}
            value={quantity ?? ""}
            onChange={({ target }) => onQuantityChange(target.value)}
          />
        ) : (
          <Input
            name="termination_settlement_quantity"
            type="text"
            label={t("termination_settlement_quantity", "Terminoitava määrä")}
            value={quantity == null || quantity === "" ? "" : thousandFormat(quantity)}
            disabled
            readOnly
          />
        )}
      </Col>
      <Col xs="12" md="4" className="mb-3">
        <Input
          name="termination_settlement_unit_price"
          type="decimal"
          decimalPlaces={DERIVATIVE_PRICE_DECIMALS}
          label={t("termination_settlement_unit_price", "Terminointi per kpl")}
          value={unitPrice ?? ""}
          className={unitClassName}
          disabled={!unitEditable}
          readOnly={!unitEditable}
          onChange={unitEditable ? ({ target }) => onUnitPriceChange(target.value) : undefined}
        />
      </Col>
      {stateDate !== undefined && (
        <Col xs="12" md="4" className="mb-3">
          <Input
            name="termination_settlement_state_date"
            type="text"
            label={t("option_edit_state_date", "Tilan päivämäärä")}
            value={stateDate ? moment(stateDate).format("D.M.YYYY") : ""}
            disabled
            readOnly
          />
        </Col>
      )}
      <Col xs="12" md="4" className="mb-3">
        <Input
          name="termination_settlement_total"
          type="decimal"
          decimalPlaces={TOTAL_DECIMAL_PLACES}
          label={t("termination_settlement_total", "Kokonais terminointihinta")}
          value={total ?? ""}
          disabled={!totalEditable}
          readOnly={!totalEditable}
          onChange={totalEditable ? ({ target }) => onTotalChange(target.value) : undefined}
        />
      </Col>
      <Col xs="12" className="mb-3 mt-n2">
        <small className="text-muted">
          {t(
            "termination_settlement_help",
            "Kappalehinta on kohde-etuuden (osake, valuutta) hinta päättämishetkellä, ei option takaisinostohinta. Jos pankki ilmoittaa maksetun tai saadun summan, kirjaa se kokonaissummaan: tulos lasketaan silloin siitä."
          )}
        </small>
      </Col>
    </Row>
  );
};

export default TerminationSettlementFields;
